import React from 'react'
import { BarChartElement, ChartDataLabelsPlugin } from './chartJS'
import { ChartProps } from 'react-chartjs-2/dist/types'
import { ChartLegend } from './legend'
import { CHART_TOOLTIP_OPTIONS, ChartLabelColoring } from './chartWrapper'
import { Legend } from './pieChart'

export interface BarChartData {
  labels: string[]
  datasets: {
    label: string
    data: number[]
    backgroundColor?: string
    borderColor?: string
    borderWidth?: number
  }[]
}

interface BarChartProps {
  data: BarChartData
  title?: string
  legend?: Legend
  className?: string
  onLabelClick?: (labels: string[]) => void
}

export const BarChart: React.FC<BarChartProps> = ({ data, title, className, legend, onLabelClick = () => {} }) => {
  const processedOptions = React.useMemo(() => {
    const options: Omit<ChartProps<'bar'>, 'type'> = {
      data: {
        labels: data.labels,
        datasets: data.datasets.map((d) => ({
          label: d.label,
          data: d.data,
          backgroundColor: d.backgroundColor || 'blue',
          borderColor: d.borderColor || 'transparent',
          borderWidth: d.borderWidth || 0,
        })),
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: CHART_TOOLTIP_OPTIONS,
          datalabels: {
            color: ChartLabelColoring({ tinyValueThreshold: 1 / 50 }),
            font: function (context) {
              const thisVal = context.dataset.data[context.dataIndex] as number
              return {
                size: thisVal < 1000 ? 12 : thisVal < 10000 ? 10 : 8,
                weight: 'bold',
              }
            },
            formatter: function (value, context) {
              return value == 0 ? '' : '' + value
            },
          },
        },
        onClick: function (e, elements) {
          onLabelClick(
            elements.flatMap((e) => [
              data.labels[e.index].toLowerCase(),
              data.datasets[e.datasetIndex].label.toLowerCase(),
            ])
          )
        },
      },
      plugins: [
        {
          id: 'clickable-labels',
          afterEvent: (chart, args, pluginOpts) => {
            if (
              args.event.type === 'click' &&
              args.event.y !== null &&
              args.event.x !== null &&
              args.event.y >= chart.scales.x.top
            ) {
              const xValue = chart.scales.x.getValueForPixel(args.event.x)
              if (
                xValue !== undefined &&
                chart.scales.x.ticks[xValue] &&
                typeof chart.scales.x.ticks[xValue].label === 'string'
              )
                onLabelClick([chart.scales.x.ticks[xValue].label.toLowerCase()])
            }
          },
        },
        ChartDataLabelsPlugin,
      ],
    }
    return options
  }, [data])

  return (
    <div className={`flex min-w-0 flex-col items-center gap-2 ${className}`}>
      {title && <div className="text-lg font-bold">{title}</div>}
      {legend && (
        <ChartLegend
          items={data.datasets.map((d) => ({
            label: d.label,
            color: d.backgroundColor || '',
          }))}
          className={legend.className}
          onLabelClick={onLabelClick}
        />
      )}
      <BarChartElement {...processedOptions} />
    </div>
  )
}

export const Histogram: React.FC<{ data: Record<number, number>; x: string; y: string }> = ({ data, x, y }) => {
  const processedOptions = React.useMemo(() => {
    const options: Omit<ChartProps<'bar'>, 'type'> = {
      data: {
        labels: Object.keys(data),
        datasets: [
          {
            label: y,
            data: Object.values(data),
            backgroundColor: '#2b7fff',
            borderColor: '#51a2ff',
            borderWidth: 3,
          },
        ],
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
            },
            title: {
              display: true,
              text: y,
              font: {
                size: 14,
                weight: 'bold',
              },
            },
          },
          x: {
            title: {
              display: true,
              text: x,
              font: {
                size: 14,
                weight: 'bold',
              },
            },
            type: 'linear',
            ticks: {
              stepSize: 1,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            ...CHART_TOOLTIP_OPTIONS,
            callbacks: {
              title: (context) => `${context[0].parsed.x} ${x}`,
              label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
            },
            displayColors: false,
          },
        },
      },
    }
    return options
  }, [data])

  return (
    <div className={`flex min-w-0 flex-col items-center gap-2`}>
      <BarChartElement {...processedOptions} />
    </div>
  )
}
